import type { NoteName } from '../types'

export const NOTE_NAMES: NoteName[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']

export const WHITE_KEYS = [0, 2, 4, 5, 7, 9, 11] // C, D, E, F, G, A, B
export const BLACK_KEYS = [1, 3, 6, 8, 10] // C#, D#, F#, G#, A#

export function getNoteName(noteIndex: number): NoteName {
  return NOTE_NAMES[noteIndex % 12]
}

export function getNoteNameFromTonic(relativeIndex: number, tonicNote: number): NoteName {
  // relativeIndex: 0 = tonic, 1 = next note, etc.
  const absoluteIndex = (tonicNote + relativeIndex) % 12
  return NOTE_NAMES[absoluteIndex]
}

export function getRelativeNoteIndex(absoluteNoteIndex: number, tonicNote: number): number {
  // Convert absolute note index (0-11) to relative index from tonic (0-11)
  return (absoluteNoteIndex - tonicNote + 12) % 12
}

export function getAbsoluteNoteIndex(relativeIndex: number, tonicNote: number): number {
  // Convert relative index from tonic (0-11) to absolute note index (0-11)
  return (tonicNote + relativeIndex) % 12
}

export function getMidiNoteInOctave(octave: number, noteIndex: number): number {
  return octave * 12 + noteIndex + 12 // C0 = 12, C4 = 60
}

export function formatCents(cents: number): string {
  if (cents === 0) return '0'
  return cents > 0 ? `+${cents.toFixed(1)}` : cents.toFixed(1)
}

// A shared cents scale (symmetric around 0), used so every note in an export
// or list is positioned on the same axis and stays easy to compare visually.
export function computeCentsScale(allCents: number[]): { min: number; max: number } {
  const minOffset = Math.min(0, ...allCents)
  const maxOffset = Math.max(0, ...allCents)
  const range = maxOffset - minOffset
  const padding = Math.max(20, range * 0.2)
  const minRounded = Math.floor((minOffset - padding) / 10) * 10
  const maxRounded = Math.ceil((maxOffset + padding) / 10) * 10
  const absMax = Math.max(Math.abs(minRounded), Math.abs(maxRounded))
  return { min: -absMax, max: absMax }
}

export function calculateFrequency(
  midiNote: number,
  offsetCents: number,
  referencePitch: number = 440
): number {
  // frequency = referencePitch * 2^((midiNote - 69 + offsetCents/100) / 12)
  return referencePitch * Math.pow(2, (midiNote - 69 + offsetCents / 100) / 12)
}

export function calculateTonicOctave(tonicNote: number, tonicFrequency: number, referencePitch: number = 440): number {
  // Calculate which octave the tonic is in based on its frequency
  // Using A4 = 440 Hz as reference
  // For the tonic note, find the octave that gives the closest frequency to tonicFrequency
  const a4MidiNote = 69 // A4
  const tonicMidiNoteInOctave4 = 4 * 12 + tonicNote + 12 // MIDI note of tonic in octave 4
  
  // Calculate expected frequency of tonic in octave 4
  const expectedFreqOctave4 = referencePitch * Math.pow(2, (tonicMidiNoteInOctave4 - a4MidiNote) / 12)
  
  // Find the octave that gives the closest frequency
  const octaveRatio = tonicFrequency / expectedFreqOctave4
  const octaveOffset = Math.round(Math.log2(octaveRatio))
  
  return 4 + octaveOffset
}

export function calculateFrequencyFromTonic(
  midiNote: number,
  offsetCents: number,
  tonicNote: number, // Note index 0-11
  tonicFrequency: number,
  referencePitch: number = 440
): number {
  // Calculate the octave of the tonic based on its frequency
  const tonicOctave = calculateTonicOctave(tonicNote, tonicFrequency, referencePitch)
  
  // Calculate the MIDI note of the tonic in its octave
  const tonicMidiNote = tonicOctave * 12 + tonicNote + 12 // C0 = 12
  
  // Calculate semitones difference from tonic (simple MIDI note difference)
  const semitonesFromTonic = midiNote - tonicMidiNote
  
  // Calculate frequency: tonicFrequency * 2^(semitonesFromTonic/12) * 2^(offsetCents/1200)
  // This directly uses the tonic frequency as the base, regardless of standard tuning
  return tonicFrequency * Math.pow(2, (semitonesFromTonic + offsetCents / 100) / 12)
}

// French note names for tuner export
const NOTE_NAMES_FR: { [key: string]: string } = {
  'C': 'Do',
  'C#': 'Do#',
  'Db': 'Réb',
  'D': 'Ré',
  'D#': 'Ré#',
  'Eb': 'Mib',
  'E': 'Mi',
  'F': 'Fa',
  'F#': 'Fa#',
  'Gb': 'Solb',
  'G': 'Sol',
  'G#': 'Sol#',
  'Ab': 'Lab',
  'A': 'La',
  'A#': 'La#',
  'Bb': 'Sib',
  'B': 'Si'
}

export function getNoteNameFrench(noteIndex: number): string {
  const noteName = NOTE_NAMES[noteIndex % 12]
  // Handle enharmonic equivalents
  if (noteIndex === 1) return 'Do#' // C#
  if (noteIndex === 3) return 'Ré#' // D#
  if (noteIndex === 6) return 'Fa#' // F#
  if (noteIndex === 8) return 'Sol#' // G#
  if (noteIndex === 10) return 'La#' // A#
  
  return NOTE_NAMES_FR[noteName] || noteName
}

